// Client (external user) vehicle page policy for AT and CZ, derived from the
// Austrian client's "Smart Forms" Excel: which fields the client must fill, and
// which fields to hide. Used by Vehicle.tsx (validation), the vehicle and
// finance forms and their read-only previews (hiding), and the finance section
// (premium timeline hidden while the vehicle is pending).

import { TaxonomyState } from "store/taxonomy";
import { UserRole } from "types/types";
import {
  COVERAGE_DATE_LINES,
  coverageDateErrorKey,
  coverageDateKeys,
  ASSISTANCE_OPTION_ERROR_KEY,
  LINE_OPTIONS,
  lineOptionErrorKey,
  LineOptionsGetters,
  parseRequestedLines,
  resolveAssistanceOptions,
  serializeCoverageDate,
  WINDSCREEN_LIMIT_ERROR_KEY,
  WINDSCREEN_SCOPE_ERROR_KEY,
} from "./FinanceTab/FinanceForm/requestedLinesHelpers";

// Countries whose client gets this page. CZ looks the same as AT, but without
// the mandatory basic vehicle data (see isClientFieldMandatory).
const CLIENT_FORM_COUNTRIES = ["Country.AT", "Country.CZ"];

export const isClientFormUser = (
  countryCode: string | undefined,
  userRole: UserRole | null
) => userRole === UserRole.external && CLIENT_FORM_COUNTRIES.includes(countryCode ?? "");

// Vehicle/general fields NOT part of the Excel — hidden for the client.
const CLIENT_HIDDEN_VEHICLE_FIELDS = new Set<string>([
  "secondLicensePlate",
  // The backend derives the main group (AT: from the legal vehicle type and the
  // usage code, CZ: from the legal vehicle type), so the client neither picks it
  // nor sends it (see the payload stripping and the validation skip in Vehicle.tsx).
  "vehicleMainGroupCode",
]);

/** Vehicle/general field hidden (and therefore not validated) for the client. */
export const isClientHiddenVehicleField = (
  fieldName: string,
  countryCode: string | undefined,
  userRole: UserRole | null
) =>
  isClientFormUser(countryCode, userRole) &&
  CLIENT_HIDDEN_VEHICLE_FIELDS.has(fieldName);

// Finance fields NOT part of the Excel — hidden for the client.
export const CLIENT_HIDDEN_FINANCE_FIELDS = new Set<string>([
  "comment",
  "vehicleIssuingStatusCode",
  "vehicleIssuingSubstatusCode",
  "vehicleIssuingComment",
  "leasingContractNumber",
  "leasingContractStartDate",
  "leasingContractEndDate",
  "leasingAmountPaidByLessee",
  "leasingAmountPaidByLesseeEur",
  "leasingCompanyAddress",
  "leasingCompanyTaxCode",
  "eurotaxCode",
  "bonusMalusCode",
  "loanAmount",
  "loanAmountEur",
  "machineryBreakdownListPrice",
  "machineryBreakdownListPriceTypeCode",
  "machineryBreakdownSpecialEquipment",
  "machineryBreakdownIsIncludingTax",
]);

// VehicleClass code groups (best-guess per the Excel; same sets used elsewhere).
const TRUCK = ["VehicleClass.FE_FK_N1", "VehicleClass.FE_FK_N2", "VehicleClass.FE_FK_N3"];
const OMNIBUS = ["VehicleClass.FE_FK_M2", "VehicleClass.FE_FK_M3"];
const MOTORCYCLE = [
  "VehicleClass.FE_FK_L1E",
  "VehicleClass.FE_FK_L2E",
  "VehicleClass.FE_FK_L3E",
  "VehicleClass.FE_FK_L4E",
  "VehicleClass.FE_FK_L5E",
];

const optionEmpty = (v: any) => !v || v.value == null;
const textEmpty = (v: any) => v == null || String(v).trim() === "";
const radioUnset = (v: any) =>
  v !== "true" && v !== "false" && v !== true && v !== false;

// Basic vehicle data always mandatory for the AT client (Excel "Mandatory: Yes");
// optional for the CZ client.
const BASIC_VEHICLE_DATA_MANDATORY = [
  "vehicleClassCode",
  "brandCode",
  "model",
  "firstRegistrationDate",
  "carUsageCode",
  "vehiclePowerTypeCode",
  "powerKw",
  "co2Emission", // 0 allowed; only empty is invalid
];

// Mandatory for every client. Relevant even without hull cover (e.g. an MTPL
// claim where the other party is at fault), so it is mandatory regardless of
// the requested lines.
const ALWAYS_MANDATORY = ["vatReimbursment"];

// Field → control kind, for the emptiness check in validation.
const FIELD_KIND: Record<string, "option" | "text" | "radio" | "date"> = {
  vehicleClassCode: "option",
  brandCode: "option",
  carUsageCode: "option",
  vehiclePowerTypeCode: "option",
  listPriceTypeCode: "option",
  firstRegistrationDate: "date",
  model: "text",
  powerKw: "text",
  co2Emission: "text",
  emptyWeightKg: "text",
  cubicCapacityCcm: "text",
  maxGrossWeightKg: "text",
  weightCapacityKg: "text",
  seats: "text",
  listPrice: "text",
  specialEquipment: "text",
  vatReimbursment: "radio",
  isIncludingTax: "radio",
  isLeasing: "radio",
};

export interface ClientCtx {
  // AT: the basic vehicle data (incl. the class / power type dependent ones) are mandatory
  basicVehicleDataMandatory: boolean;
  hull: boolean;
  accident: boolean;
  isTruck: boolean;
  isOmnibus: boolean;
  isMotorcycle: boolean;
  isElectric: boolean;
}

/** Derives the client conditional context from the live form values. */
export const buildClientCtx = (
  values: Record<string, any>,
  taxonomy: TaxonomyState,
  countryCode: string | undefined
): ClientCtx => {
  const lines = parseRequestedLines(values?.clientCoveragesInfo);
  const classCode = values?.vehicleClassCode?.value
    ? (taxonomy as any)?.VehicleClass?.byId?.[values.vehicleClassCode.value]?.code
    : null;
  const powerCode = values?.vehiclePowerTypeCode?.value
    ? (taxonomy as any)?.VehiclePowerType?.byId?.[
        values.vehiclePowerTypeCode.value
      ]?.code
    : null;
  return {
    basicVehicleDataMandatory: countryCode === "Country.AT",
    hull: lines.hull,
    accident: lines.accident,
    isTruck: TRUCK.includes(classCode),
    isOmnibus: OMNIBUS.includes(classCode),
    isMotorcycle: MOTORCYCLE.includes(classCode),
    isElectric: !!powerCode && powerCode.includes("ELEKTRO"),
  };
};

/**
 * Single source of truth for "is this field mandatory for the client",
 * used by both the validation and the required-asterisk in the form.
 */
export const isClientFieldMandatory = (
  name: string,
  ctx: ClientCtx | null | undefined
): boolean => {
  if (!ctx) return false;
  if (ALWAYS_MANDATORY.includes(name)) return true;
  if (ctx.basicVehicleDataMandatory && BASIC_VEHICLE_DATA_MANDATORY.includes(name))
    return true;
  switch (name) {
    case "emptyWeightKg":
      return ctx.basicVehicleDataMandatory && ctx.isElectric;
    case "cubicCapacityCcm":
      return ctx.basicVehicleDataMandatory && ctx.isMotorcycle;
    case "maxGrossWeightKg":
    case "weightCapacityKg":
      return ctx.basicVehicleDataMandatory && ctx.isTruck;
    case "seats":
      return ctx.basicVehicleDataMandatory && (ctx.isOmnibus || ctx.accident);
    // Hull values (rendered in the finance section) — required when hull chosen.
    // Optional for the CZ client.
    case "specialEquipment":
      return ctx.basicVehicleDataMandatory && ctx.hull;
    case "listPrice":
    case "listPriceTypeCode":
    case "isIncludingTax":
    case "isLeasing":
      return ctx.hull;
    default:
      return false;
  }
};

/**
 * Returns Formik errors for the client's mandatory fields (per the Excel),
 * keyed by field name so they surface on the corresponding controls.
 */
export const validateClientVehicle = (
  values: Record<string, any>,
  taxonomy: TaxonomyState,
  countryCode: string | undefined,
  requiredMsg: string,
  coverageDates?: CoverageDateValidationOpts
): Record<string, string> => {
  const errors: Record<string, string> = {};
  const ctx = buildClientCtx(values, taxonomy, countryCode);

  Object.entries(FIELD_KIND).forEach(([name, kind]) => {
    if (!isClientFieldMandatory(name, ctx)) return;
    const v = values?.[name];
    const empty =
      kind === "option"
        ? optionEmpty(v)
        : kind === "radio"
        ? radioUnset(v)
        : kind === "date"
        ? !v
        : textEmpty(v);
    if (empty) errors[name] = requiredMsg;
  });

  // Hull sub-type (Teilkasko/Vollkasko) — required when hull is chosen.
  if (ctx.hull && !parseRequestedLines(values?.clientCoveragesInfo).hullVariant) {
    errors.clientCoveragesInfo = requiredMsg;
  }

  if (countryCode === "Country.CZ" && coverageDates) {
    Object.assign(
      errors,
      validateCoverageDates(values?.clientCoveragesInfo, coverageDates)
    );
    if (coverageDates.getLineOptions) {
      Object.assign(
        errors,
        validateLineOptions(
          values?.clientCoveragesInfo,
          coverageDates.getLineOptions,
          requiredMsg
        )
      );
    }
    // the requested Assistance's option, if there are any for it
    const lines = parseRequestedLines(values?.clientCoveragesInfo);
    const assistance = resolveAssistanceOptions(
      lines,
      coverageDates.getAssistanceOptions
    );
    if (
      assistance &&
      !(lines.assistanceOption && assistance.options.includes(lines.assistanceOption))
    ) {
      errors[ASSISTANCE_OPTION_ERROR_KEY] = requiredMsg;
    }
    // the windscreen coverage's scope and limit
    if (lines.windscreen) {
      if (!lines.windscreenScope) errors[WINDSCREEN_SCOPE_ERROR_KEY] = requiredMsg;
      if (!(lines.windscreenLimit != null && lines.windscreenLimit > 0)) {
        errors[WINDSCREEN_LIMIT_ERROR_KEY] = requiredMsg;
      }
    }
  }

  return errors;
};

export interface CoverageDateValidationOpts {
  /** clientCoveragesInfo as saved (edit), null on create / duplicate. */
  savedCoveragesInfo?: string | null;
  toBeforeFromMsg: string;
  fromInPastMsg: string;
  /** The options offered per line for its coverage start (CZ: MTPL limits, hull deductible options). */
  getLineOptions?: LineOptionsGetters;
  /** Per line, the assistance options of the line's main insurer for a coverage start (CZ). */
  getAssistanceOptions?: LineOptionsGetters;
}

// CZ: with a requested MTPL / Hull and its coverage start, one of the line's
// options applicable on that day (MTPL insurance limit of the main MTPL
// insurer, hull deductible option of the main Hull insurer) must be picked, if
// there are any.
const validateLineOptions = (
  coveragesInfo: string | null | undefined,
  getLineOptions: LineOptionsGetters,
  requiredMsg: string
): Record<string, string> => {
  const errors: Record<string, string> = {};
  const lines = parseRequestedLines(coveragesInfo);
  COVERAGE_DATE_LINES.forEach((line) => {
    const from = lines[coverageDateKeys(line).from];
    const getOptions = getLineOptions[line];
    if (!lines[line] || !from || !getOptions) return;
    const options = getOptions(from);
    const picked = lines[LINE_OPTIONS[line].key];
    if (options.length > 0 && !(picked && options.includes(picked))) {
      errors[lineOptionErrorKey(LINE_OPTIONS[line].key)] = requiredMsg;
    }
  });
  return errors;
};

// CZ: the requested coverage dates are optional; "to" must not be before
// "from", and a new or changed "from" must not be in the past (an unchanged
// one of an existing vehicle is accepted, so it can still be saved later).
const validateCoverageDates = (
  coveragesInfo: string | null | undefined,
  opts: CoverageDateValidationOpts
): Record<string, string> => {
  const errors: Record<string, string> = {};
  const lines = parseRequestedLines(coveragesInfo);
  const savedLines = parseRequestedLines(opts.savedCoveragesInfo);
  const today = serializeCoverageDate(new Date())!;
  COVERAGE_DATE_LINES.filter((line) => lines[line]).forEach((line) => {
    const { from, to } = coverageDateKeys(line);
    const fromValue = lines[from];
    const toValue = lines[to];
    // "YYYY-MM-DD" strings compare chronologically
    if (fromValue && fromValue < today && fromValue !== savedLines[from]) {
      errors[coverageDateErrorKey(from)] = opts.fromInPastMsg;
    }
    if (fromValue && toValue && toValue < fromValue) {
      errors[coverageDateErrorKey(to)] = opts.toBeforeFromMsg;
    }
  });
  return errors;
};
